import { FormInstance, message } from 'antd'
import { isEqual } from 'lodash'
import _ from 'lodash'
import { PropertyType, RentalInfoPeriod } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { PTOTypeEnum, TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import { usePTOContext } from 'context/pto-context-provider'
import {
  cleanCoTrustedPartners,
  cleanGoldenTickets,
  cleanMetadata,
  INITIAL_CO_TRUSTED_PARTNER,
  INITIAL_GOLDEN_TICKET,
  initTimeLinesEdit,
  normalizeTermsContent,
  toRentalPeriodPayload,
} from 'extends/pto.extend'
import usePrevious from 'hooks/use-previous'
import { logError, logInfo } from 'utils/logger'

interface UseEditPTOProps {
  form: FormInstance
}

const useEditPTO = ({ form }: UseEditPTOProps) => {
  const [edit, setEdit] = useState(false)
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [defaultPropertyType, setDefaultPropertyType] = useState<PropertyType | null>(null)
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [switchChecked, setSwitchChecked] = useState(false)

  const { code, ptoDetail, getPTODetail } = usePTOContext()
  const previewPtoDetail = usePrevious(ptoDetail)

  const initFormPTODetail = useCallback(async () => {
    try {
      if (!code || !ptoDetail) return
      setSwitchChecked(ptoDetail.status === TokenizeStatusEnum.ACTIVE)
      const resGetListProperty = await AppAdmin.getListProperty()
      const types: PropertyType[] = []
      if (resGetListProperty?.data.success) {
        resGetListProperty?.data.data.map((item: PropertyType) => {
          types.push({
            ...item,
            id: Number(item.id),
          } as PropertyType)
        })
        setPropertyTypes(types)
      }
      const propertyItems: any = {}
      const selectedType = types.find((type: any) => type.id === ptoDetail?.type)
      if (selectedType) {
        setDefaultPropertyType(selectedType)
        selectedType.properties?.map((prop: any) => {
          propertyItems[`${prop}`] = ptoDetail?.propertyItems?.[`${prop}`]
        })
      }

      const formValues = {
        code,
        name: ptoDetail?.fullName,
        terms: normalizeTermsContent(ptoDetail?.terms),
        type: ptoDetail?.type,
        country: ptoDetail?.country,
        contractAddress: ptoDetail?.contractAddress,
        ownerAddress: ptoDetail?.ownerAddress,
        description: ptoDetail?.description,
        latitude: ptoDetail?.latitude,
        longitude: ptoDetail?.longitude,
        status: ptoDetail?.status === TokenizeStatusEnum.ACTIVE,
        marketPlaceSC: ptoDetail?.marketPlaceSC,
        isFeatured: ptoDetail?.isFeatured,
        isShowDetail: ptoDetail.isShowDetail,
        isShowList: ptoDetail.isShowList,
        propertyItems: propertyItems,
        metadata: !_.isEmpty(ptoDetail?.metadata) ? ptoDetail?.metadata : {},
        metadataUrl: ptoDetail?.metadataUrl,
        marketplaceEnable: ptoDetail?.marketplaceEnable || false,
        tradeUnlockTime: ptoDetail?.tradeUnlockTime,
        maxNFTSupply: ptoDetail?.maxNFTSupply,
        pricePerNFT: ptoDetail?.pricePerNFT,
        royalty: ptoDetail?.royaltyFee,
        transactionFee: ptoDetail?.buyingFeeBP,
        phases: initTimeLinesEdit(ptoDetail?.phases),
        goldenTickets: ptoDetail?.goldenTickets?.map((item: any) => ({
          ...item,
          isNewRecord: false,
        })) || [INITIAL_GOLDEN_TICKET],
        typeSocial: ptoDetail?.typeSocial || {},
        ptoType: ptoDetail?.ptoType,
        rentalPeriods: ptoDetail?.revenueSharingRental || [],
        isLeasingInProgress: ptoDetail?.isLeasingInProgress || false,
        coTrustedPartners: ptoDetail?.coTrustedPartners?.length
          ? ptoDetail.coTrustedPartners
          : [INITIAL_CO_TRUSTED_PARTNER],
      }

      logInfo('formValues', formValues)

      form.setFieldsValue(formValues)

      setThumbnail(ptoDetail.thumbnail)
      setThumbnails(ptoDetail.thumbnails)
    } catch (error) {
      logError('Error fetching details: ', error)
    }
  }, [code, form, ptoDetail])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const {
        code,
        type,
        name,
        country,
        contractAddress,
        description = '',
        latitude,
        isShowDetail,
        isShowList,
        longitude,
        status,
        isFeatured,
        propertyItems,
        marketplaceEnable,
        metadata: formMetadata,
        tradeUnlockTime,
        goldenTickets: formGoldenTickets,
        typeSocial,
        rentalPeriods,
        isLeasingInProgress,
        ptoType,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const metadata = cleanMetadata(formMetadata)
      const goldenTickets = cleanGoldenTickets(formGoldenTickets)
      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      const params = {
        fullName: name,
        status: status ? TokenizeStatusEnum.ACTIVE : TokenizeStatusEnum.INACTIVE,
        country: country,
        contractAddress: contractAddress || null,
        description: description,
        isFeatured: !!isFeatured,
        isShowDetail,
        isShowList,
        type: type,
        latitude: latitude,
        longitude: longitude,
        propertyItems: propertyItems,
        marketplaceEnable: marketplaceEnable ? Boolean(marketplaceEnable) || false : false,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        metadata: metadata,
        tradeUnlockTime: tradeUnlockTime || 0,
        goldenTickets,
        typeSocial: typeSocial || {},
        isLeasingInProgress: !!isLeasingInProgress,
        terms: {
          flowType: TermsFlowEnum.PURCHASE,
          content: terms || '',
        },
        coTrustedPartners,
      }
      await AppAdmin.ptoUpdate(code, params)

      if (ptoType === PTOTypeEnum.RENTAL_PTO && rentalPeriods?.length) {
        const originalPeriods = ptoDetail?.revenueSharingRental || []

        for (const period of rentalPeriods as RentalInfoPeriod[]) {
          const payload = toRentalPeriodPayload(period)

          if (period._id) {
            const original = originalPeriods.find((p: RentalInfoPeriod) => p._id === period._id)
            if (!isEqual(original, period)) {
              const { _id, ...updatePayload } = payload

              const finalPayload = period.contractAddress
                ? { annualNetCashflowToInvestor: updatePayload.annualNetCashflowToInvestor }
                : updatePayload
              await AppAdmin.updateRentalInfo(code, period._id, finalPayload)
            }
          } else {
            await AppAdmin.createRentalInfo(code, payload)
          }
        }
      }

      message.success('PTO updated successfully!')
      getPTODetail()
      setEdit(false)
    } catch (error: any) {
      message.error(error?.message ? error?.message : 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    initFormPTODetail()
    setEdit(false)
  }

  const handleEdit = () => {
    setEdit(true)
  }

  useEffect(() => {
    if (ptoDetail && !isEqual(ptoDetail, previewPtoDetail)) {
      initFormPTODetail()
    }
  }, [initFormPTODetail, previewPtoDetail, ptoDetail])

  return {
    edit,
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    defaultPropertyType,
    propertyTypes,
    switchChecked,
    setSwitchChecked,
    ptoDetail,
    code,
    handleSubmit,
    handleCancel,
    handleEdit,
  }
}

export default useEditPTO
